// src/modules/marketplace/components/kraken/overviewChart.tsx

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { Box, Typography, useTheme } from "@mui/material"
import { Kraken } from "../../interfaces"
import { formatBucket, formatNumber } from "./utils"

interface OverviewChartProps {
  points: Kraken.PeriodPoint[]
  period: Kraken.Period
}

const LABELS: Record<string, string> = {
  loaded: "Загружено",
  sold_kraken: "Продано (kraken)",
  sold_liga: "Продано (liga)",
  sold_other: "Продано (прочие)",
  conversion_percent: "Конверсия",
}

export function OverviewChart({ points, period }: OverviewChartProps) {
  const theme = useTheme()
  const gridColor =
    theme.palette.mode === "light"
      ? theme.palette.grey[300]
      : theme.palette.grey[700]

  if (!points.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Нет данных за период
      </Typography>
    )
  }

  return (
    <Box>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart
          data={points}
          margin={{ top: 8, right: 24, left: 12, bottom: 30 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey="period_start"
            tickFormatter={(v) => formatBucket(v, period)}
            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
            angle={-45}
            textAnchor="end"
            interval={Math.max(Math.floor(points.length / 20), 0)}
          />
          <YAxis
            yAxisId="count"
            orientation="left"
            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
            tickFormatter={(v) => formatNumber(v)}
            label={{
              value: "Количество, шт",
              angle: -90,
              position: "insideLeft",
              style: {
                fontSize: 11,
                fill: theme.palette.text.secondary,
                textAnchor: "middle",
              },
            }}
          />
          <YAxis
            yAxisId="percent"
            orientation="right"
            domain={[0, 100]}
            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
            tickFormatter={(v) => `${v}%`}
            label={{
              value: "Конверсия, %",
              angle: 90,
              position: "insideRight",
              style: {
                fontSize: 11,
                fill: theme.palette.text.secondary,
                textAnchor: "middle",
              },
            }}
          />
          <Tooltip
            labelFormatter={(label) => formatBucket(String(label), period)}
            formatter={(
              value: number | undefined,
              name: string | undefined,
            ) => [
              name === "conversion_percent"
                ? `${value ?? 0}%`
                : formatNumber(value),
              (name && LABELS[name]) ?? name,
            ]}
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${gridColor}`,
              borderRadius: 4,
              fontSize: 12,
            }}
          />
          <Legend
            formatter={(value) => LABELS[value] ?? value}
            wrapperStyle={{ fontSize: 12 }}
          />
          <Bar
            yAxisId="count"
            dataKey="loaded"
            stackId="loaded"
            fill={theme.palette.info.main}
            opacity={0.7}
            radius={[2, 2, 0, 0]}
          />
          <Bar
            yAxisId="count"
            dataKey="sold_kraken"
            stackId="sold"
            fill={theme.palette.success.main}
          />
          <Bar
            yAxisId="count"
            dataKey="sold_liga"
            stackId="sold"
            fill="#f57c00"
          />
          <Bar
            yAxisId="count"
            dataKey="sold_other"
            stackId="sold"
            fill={theme.palette.grey[500]}
            radius={[2, 2, 0, 0]}
          />
          <Line
            yAxisId="percent"
            dataKey="conversion_percent"
            stroke={theme.palette.error.main}
            strokeWidth={2}
            strokeDasharray="5 3"
            dot={false}
            type="monotone"
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  )
}
